// M&A synergy candidate map read-model (charter map row 33).
//
// The candidate register renders CLAIMED overlaps in the two-register grammar:
// `certified` (c0 observed / c1 committed — GOVERNED_SYNERGY_CERTAINTY_CLASSES)
// versus `candidate` (c2 modelled / c3 hypothesis / blocked). The registers
// never sum: the cross-register total is structurally withheld (`null`), the
// same law the golden worksheet pins with `displayedTotalBaseGrossUsd = null`.
//
// This module owns zero numeric authorship: every dollar re-derives from the
// validated `SynergyLeverCommitment` bands (net = gross − cost-to-achieve,
// never a free-standing figure), and target-pack overlap zones that carry no
// sized commitment render as unsized claims — never as invented dollars.

import type { MaTargetPack, MaTargetPackCertaintyClass } from './ma-target-packs';
import { MA_TARGET_PACK_SCHEMA_VERSION } from './ma-target-packs';
import {
  SYNERGY_CERTAINTY_CLASSES,
  isGovernedSynergyCertaintyClass,
  validateSynergyLeverCommitment,
  type SynergyCertaintyClass,
  type SynergyLeverCommitment,
  type SynergyUnderwritingBands,
  type SynergyValueType,
} from './ma-synergy-commitment';

export const MA_SYNERGY_CANDIDATE_MAP_SCHEMA_VERSION =
  'ma_synergy_candidate_map_v1' as const;

export const SYNERGY_REGISTER_KINDS = ['certified', 'candidate'] as const;

export type SynergyRegisterKind = (typeof SYNERGY_REGISTER_KINDS)[number];

export const SYNERGY_CANDIDATE_MAP_PRIORITY_BASIS =
  'Ordered by certainty class (C0 observed → C1 committed → C2 modelled → C3 hypothesis → ' +
  'blocked), then base net run-rate (gross − cost-to-achieve) descending; entries failing ' +
  'footprint deconfliction sink within their class. Certified and candidate dollars are ' +
  'separate registers and are never summed.';

export type SynergyCandidateMapEntry = Readonly<{
  synergyLeverId: string;
  title: string;
  registerKind: SynergyRegisterKind;
  certaintyClass: SynergyCertaintyClass;
  valueType: SynergyValueType;
  ownerLabel: string;
  workstreamId: string;
  /** Evidence family that proves realization (`realizationEvidence.sourceContextKind`). */
  evidenceFamily: string;
  sizingEvidenceRefIds: readonly string[];
  bands: SynergyUnderwritingBands;
  /** Computed: base gross run-rate − one-time cost-to-achieve. Never authored. */
  baseNetRunRateUsd: number;
  footprintStatus: 'attributed' | 'unattributed';
  footprintAtomCount: number;
  /** Footprint atoms with `atomKind === 'position'` — the seat-level footprint. */
  footprintSeatCount: number;
  overlapDimensionKeys: readonly string[];
  /** Named gaps blocking promotion into the certified register. Empty when certified. */
  promotionGapCodes: readonly string[];
  /** Deterministic "what would prove it" sentence. Null when nothing blocks promotion. */
  promotionProof: string | null;
  /** 1-based position in the recommendation-priority ordering. */
  recommendationRank: number;
  /**
   * False when the entry may not contribute dollars to its register total:
   * hypotheses are never priced, blocked levers render as gaps (never zero),
   * and footprint collisions withhold both colliding levers.
   */
  countsTowardRegisterTotals: boolean;
}>;

export type SynergyCandidateMapUnsizedClaim = Readonly<{
  claimId: string;
  label: string;
  certaintyClass: MaTargetPackCertaintyClass;
  sourceRefs: readonly string[];
  gapCode: 'zone_not_underwritten';
  proofRequirement: string;
}>;

export type SynergyCandidateMapRejectedCommitment = Readonly<{
  synergyLeverId: string;
  errors: readonly string[];
}>;

export type SynergyCandidateFootprintCollision = Readonly<{
  /** `atomKind:atomId:scope` — no footprint atom may be drawn by two levers. */
  atomKey: string;
  firstLeverId: string;
  secondLeverId: string;
}>;

export type SynergyCandidateRegisterTotals = Readonly<{
  certifiedObservedBaseGrossUsd: number;
  certifiedCommittedBaseGrossUsd: number;
  candidateModelledBaseGrossUsd: number;
  hypothesisCount: number;
  blockedCount: number;
  /** Structurally withheld: certified and candidate registers never sum. */
  displayedCrossRegisterTotalUsd: null;
}>;

export type SynergyCandidateRegister = Readonly<{
  schemaVersion: typeof MA_SYNERGY_CANDIDATE_MAP_SCHEMA_VERSION;
  dealId: string;
  asOf: string;
  targetPackStatus: 'attached' | 'absent' | 'unsupported_schema';
  targetPackId: string | null;
  targetLegalName: string | null;
  priorityBasis: string;
  /** Ordered by recommendation priority (see `priorityBasis`). */
  entries: readonly SynergyCandidateMapEntry[];
  unsizedClaimedOverlaps: readonly SynergyCandidateMapUnsizedClaim[];
  rejectedCommitments: readonly SynergyCandidateMapRejectedCommitment[];
  footprintCollisions: readonly SynergyCandidateFootprintCollision[];
  totals: SynergyCandidateRegisterTotals;
}>;

export type BuildSynergyCandidateRegisterInput = Readonly<{
  dealId: string;
  asOf: string;
  commitments: readonly unknown[];
  targetPack?: MaTargetPack | null;
}>;

export function synergyRegisterKindFor(
  certaintyClass: SynergyCertaintyClass,
): SynergyRegisterKind {
  return isGovernedSynergyCertaintyClass(certaintyClass) ? 'certified' : 'candidate';
}

export function buildSynergyCandidateRegister(
  input: BuildSynergyCandidateRegisterInput,
): SynergyCandidateRegister {
  const accepted: SynergyLeverCommitment[] = [];
  const rejectedCommitments: SynergyCandidateMapRejectedCommitment[] = [];

  input.commitments.forEach((raw, index) => {
    const validation = validateSynergyLeverCommitment(raw);
    if (!validation.ok) {
      rejectedCommitments.push({
        synergyLeverId: rejectedLeverId(raw, index),
        errors: validation.errors,
      });
      return;
    }
    if (validation.value.dealId !== input.dealId) {
      rejectedCommitments.push({
        synergyLeverId: validation.value.synergyLeverId,
        errors: [
          `commitment dealId ${validation.value.dealId} does not match register deal ` +
            `${input.dealId}`,
        ],
      });
      return;
    }
    accepted.push(validation.value);
  });

  const footprintCollisions = findFootprintCollisions(accepted);
  const collidingLeverIds = new Set(
    footprintCollisions.flatMap((collision) => [
      collision.firstLeverId,
      collision.secondLeverId,
    ]),
  );

  const unranked = accepted.map((commitment) =>
    entryFromCommitment(commitment, collidingLeverIds.has(commitment.synergyLeverId)),
  );
  const entries = sortByRecommendationPriority(unranked).map((entry, index) => ({
    ...entry,
    recommendationRank: index + 1,
  }));

  const pack = input.targetPack ?? null;
  const supportedPack =
    pack && pack.schemaVersion === MA_TARGET_PACK_SCHEMA_VERSION ? pack : null;

  return {
    schemaVersion: MA_SYNERGY_CANDIDATE_MAP_SCHEMA_VERSION,
    dealId: input.dealId,
    asOf: input.asOf,
    targetPackStatus: pack
      ? supportedPack
        ? 'attached'
        : 'unsupported_schema'
      : 'absent',
    targetPackId: supportedPack ? supportedPack.packId : null,
    targetLegalName: supportedPack ? supportedPack.identity.legalName.value : null,
    priorityBasis: SYNERGY_CANDIDATE_MAP_PRIORITY_BASIS,
    entries,
    unsizedClaimedOverlaps: supportedPack ? unsizedClaimsFromPack(supportedPack) : [],
    rejectedCommitments,
    footprintCollisions,
    totals: registerTotals(entries),
  };
}

export function findFootprintCollisions(
  commitments: readonly SynergyLeverCommitment[],
): readonly SynergyCandidateFootprintCollision[] {
  const seen = new Map<string, string>();
  const violations: SynergyCandidateFootprintCollision[] = [];

  for (const commitment of commitments) {
    if (commitment.poolFootprint.footprintStatus !== 'attributed') continue;
    for (const atom of commitment.poolFootprint.atoms) {
      const atomKey = `${atom.atomKind}:${atom.atomId}:${atom.scope}`;
      const firstLeverId = seen.get(atomKey);
      if (firstLeverId && firstLeverId !== commitment.synergyLeverId) {
        violations.push({
          atomKey,
          firstLeverId,
          secondLeverId: commitment.synergyLeverId,
        });
        continue;
      }
      seen.set(atomKey, commitment.synergyLeverId);
    }
  }

  return violations;
}

function entryFromCommitment(
  commitment: SynergyLeverCommitment,
  hasFootprintCollision: boolean,
): Omit<SynergyCandidateMapEntry, 'recommendationRank'> {
  const promotionGapCodes = promotionGapCodesFor(commitment, hasFootprintCollision);
  const registerKind = synergyRegisterKindFor(commitment.certaintyClass);
  const atoms =
    commitment.poolFootprint.footprintStatus === 'attributed'
      ? commitment.poolFootprint.atoms
      : [];

  return {
    synergyLeverId: commitment.synergyLeverId,
    title: commitment.title,
    registerKind,
    certaintyClass: commitment.certaintyClass,
    valueType: commitment.valueType,
    ownerLabel: commitment.owner.ownerLabel,
    workstreamId: commitment.owner.workstreamId,
    evidenceFamily: commitment.realizationEvidence.sourceContextKind,
    sizingEvidenceRefIds: commitment.sizingEvidenceRefIds,
    bands: commitment.bands,
    baseNetRunRateUsd: roundCurrency(
      commitment.bands.baseRunRateUsd - commitment.bands.oneTimeCostUsd,
    ),
    footprintStatus: commitment.poolFootprint.footprintStatus,
    footprintAtomCount: atoms.length,
    footprintSeatCount: atoms.filter((atom) => atom.atomKind === 'position').length,
    overlapDimensionKeys: commitment.overlapDimensionKeys,
    promotionGapCodes,
    promotionProof: promotionProofFor(commitment, promotionGapCodes),
    countsTowardRegisterTotals:
      !hasFootprintCollision &&
      commitment.certaintyClass !== 'c3_hypothesis' &&
      commitment.certaintyClass !== 'blocked',
  };
}

function promotionGapCodesFor(
  commitment: SynergyLeverCommitment,
  hasFootprintCollision: boolean,
): readonly string[] {
  const codes = new Set<string>(commitment.blockingGapCodes);

  if (hasFootprintCollision) codes.add('footprint_atom_collision');
  if (commitment.certaintyClass === 'c3_hypothesis') codes.add('not_priced_hypothesis');

  if (!isGovernedSynergyCertaintyClass(commitment.certaintyClass)) {
    if (commitment.sizingEvidenceRefIds.length === 0) codes.add('sizing_evidence_missing');
    if (commitment.poolFootprint.footprintStatus !== 'attributed') {
      codes.add('pool_footprint_unattributed');
    }
    if (commitment.overlapDimensionKeys.length === 0) {
      codes.add('overlap_dimensions_undeclared');
    }
    if (commitment.realizationCurve.length < 2) codes.add('realization_curve_incomplete');
  }

  return Array.from(codes).sort();
}

function promotionProofFor(
  commitment: SynergyLeverCommitment,
  promotionGapCodes: readonly string[],
): string | null {
  if (promotionGapCodes.length === 0) return null;
  const phrases = promotionGapCodes.map((code) => promotionGapPhrase(code, commitment));
  return `Promotion requires: ${phrases.join('; ')}. Promotion into the certified register happens only through the approval act.`;
}

function promotionGapPhrase(code: string, commitment: SynergyLeverCommitment): string {
  const metricKey = commitment.realizationEvidence.metricKey;
  const observableSource = commitment.realizationEvidence.observableSource;
  switch (code) {
    case 'no_observed_actual':
      return `reconcile a ${observableSource} actual for ${metricKey}`;
    case 'sizing_evidence_missing':
      return 'attach at least one sizing evidence reference';
    case 'pool_footprint_unattributed':
      return 'attribute the pool footprint to named atoms';
    case 'overlap_dimensions_undeclared':
      return 'declare overlap dimension keys for deconfliction';
    case 'realization_curve_incomplete':
      return 'commit a realization curve with at least two points';
    case 'footprint_atom_collision':
      return 'resolve the footprint atom claimed by two levers before any dollars count';
    case 'not_priced_hypothesis':
      return 'the hypothesis is not priced and stays out of all dollar totals';
    default:
      return code.replace(/_/g, ' ');
  }
}

function sortByRecommendationPriority(
  entries: readonly Omit<SynergyCandidateMapEntry, 'recommendationRank'>[],
): readonly Omit<SynergyCandidateMapEntry, 'recommendationRank'>[] {
  return [...entries].sort((left, right) => {
    const classDelta =
      certaintyClassRank(left.certaintyClass) - certaintyClassRank(right.certaintyClass);
    if (classDelta !== 0) return classDelta;
    if (left.countsTowardRegisterTotals !== right.countsTowardRegisterTotals) {
      return left.countsTowardRegisterTotals ? -1 : 1;
    }
    if (left.baseNetRunRateUsd !== right.baseNetRunRateUsd) {
      return right.baseNetRunRateUsd - left.baseNetRunRateUsd;
    }
    return left.synergyLeverId.localeCompare(right.synergyLeverId);
  });
}

function certaintyClassRank(certaintyClass: SynergyCertaintyClass): number {
  return SYNERGY_CERTAINTY_CLASSES.indexOf(certaintyClass);
}

function registerTotals(
  entries: readonly SynergyCandidateMapEntry[],
): SynergyCandidateRegisterTotals {
  const counting = entries.filter((entry) => entry.countsTowardRegisterTotals);
  return {
    certifiedObservedBaseGrossUsd: sumBaseGross(counting, 'c0_observed'),
    certifiedCommittedBaseGrossUsd: sumBaseGross(counting, 'c1_committed_lever'),
    candidateModelledBaseGrossUsd: sumBaseGross(counting, 'c2_modelled_upside'),
    hypothesisCount: entries.filter(
      (entry) => entry.certaintyClass === 'c3_hypothesis',
    ).length,
    blockedCount: entries.filter((entry) => entry.certaintyClass === 'blocked').length,
    displayedCrossRegisterTotalUsd: null,
  };
}

function unsizedClaimsFromPack(
  pack: MaTargetPack,
): readonly SynergyCandidateMapUnsizedClaim[] {
  return pack.dealThesis.expectedOverlapZones.map((zone, index) => ({
    claimId: `${pack.packId}:overlap-zone:${index}`,
    label: zone.value,
    certaintyClass: zone.certaintyClass,
    sourceRefs: zone.sourceRefs,
    gapCode: 'zone_not_underwritten',
    proofRequirement:
      'No commitment in this register declares a contract-level binding to this claimed ' +
      'zone. Underwriting it into a sized SynergyLeverCommitment (owner, bands, footprint, ' +
      'realization evidence) is what turns the zone into a candidate dollar.',
  }));
}

function sumBaseGross(
  entries: readonly SynergyCandidateMapEntry[],
  certaintyClass: SynergyCertaintyClass,
): number {
  return roundCurrency(
    entries
      .filter((entry) => entry.certaintyClass === certaintyClass)
      .reduce((total, entry) => total + entry.bands.baseRunRateUsd, 0),
  );
}

function rejectedLeverId(raw: unknown, index: number): string {
  if (
    typeof raw === 'object' &&
    raw !== null &&
    !Array.isArray(raw) &&
    typeof (raw as Record<string, unknown>).synergyLeverId === 'string' &&
    ((raw as Record<string, unknown>).synergyLeverId as string).trim().length > 0
  ) {
    return (raw as Record<string, unknown>).synergyLeverId as string;
  }
  return `unidentified_commitment_${index}`;
}

function roundCurrency(value: number): number {
  return Math.round(value * 100) / 100;
}
